/**
 * PERF-GPU-01 (decision N-7) — per-image downscale warning predicate.
 *
 * When the CURRENT image's native width OR height exceeds the device's
 * detected `MAX_TEXTURE_SIZE`, the browser silently downscales it in the
 * viewer — the inspector believes they see full resolution but does not.
 * This hook makes that condition observable per image; the chrome renders
 * the ⚠️ indicator from it (`ViewerReadouts`).
 *
 * Unknown capability (`maxTextureSize: null`, e.g. no WebGL) ⇒ NO warning
 * — unknown ≠ under-spec; detection logs `unknown` for the fleet telemetry
 * instead (see `getGpuCapabilities`).
 *
 * `readCaps` is injectable purely as a test seam — production callers use
 * the memoised `getGpuCapabilities` default, which the Viewer primitive
 * already triggered at init.
 */
import { useMemo } from "react";
import { getGpuCapabilities, type GpuCapabilities } from "@aeriosiq/ui";
import type { ImageRef } from "@aeriosiq/types";

/** Pure predicate — exported for the unit matrix. */
export function imageExceedsMaxTexture(
  image: Pick<ImageRef, "width" | "height"> | null,
  caps: Pick<GpuCapabilities, "maxTextureSize">,
): boolean {
  if (!image || caps.maxTextureSize == null) return false;
  return (
    image.width > caps.maxTextureSize || image.height > caps.maxTextureSize
  );
}

export function useGpuDownscaleWarning(
  activeImage: Pick<ImageRef, "width" | "height"> | null,
  readCaps: () => GpuCapabilities = getGpuCapabilities,
): boolean {
  return useMemo(
    () => imageExceedsMaxTexture(activeImage, readCaps()),
    [activeImage, readCaps],
  );
}
